import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  FileText, CheckCircle, ListChecks, Link2, ExternalLink,
  ChevronDown, ChevronUp, Sparkles, Loader2, Calendar
} from "lucide-react";
import { supabase } from "../../lib/supabase";

export default function SessionMaterialsView({ booking }) {
  const [materials, setMaterials] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("session_materials")
        .select("session_summary, action_plan, resource_links, status, published_at, expert_name, session_topic, session_date")
        .eq("booking_id", booking.id)
        .eq("status", "published")
        .maybeSingle();
      setMaterials(data);
      setLoading(false);
    };
    load();
  }, [booking.id]);

  if (loading) {
    return (
      <div id="session-materials-view_loading" className="p-4 text-center">
        <Loader2 className="w-6 h-6 text-primary-400 animate-spin mx-auto" />
      </div>
    );
  }

  if (!materials) return null;

  const actionPlan = Array.isArray(materials.action_plan) ? materials.action_plan : [];
  const resources = Array.isArray(materials.resource_links) ? materials.resource_links : [];

  return (
    <motion.div
      id={`session-materials_${booking.id}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-md border border-primary-600/20 bg-primary-950/10 overflow-hidden"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-primary-950/20 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-primary-600/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary-400" />
          </div>
          <span className="text-lg font-medium text-white">
            Материалы от эксперта
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-neutral-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-neutral-500" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-4">
              {materials.session_summary && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-primary-400" />
                    <span className="text-lg font-extralight text-neutral-400">Итог сессии</span>
                  </div>
                  <p className="text-lg font-extralight text-neutral-200 leading-relaxed whitespace-pre-line">
                    {materials.session_summary}
                  </p>
                </div>
              )}

              {actionPlan.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="w-4 h-4 text-accent-400" />
                    <span className="text-lg font-extralight text-neutral-400">План действий</span>
                  </div>
                  <div className="space-y-2">
                    {actionPlan.map((step, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-neutral-800/30">
                        <CheckCircle className="w-4 h-4 text-accent-400 flex-shrink-0 mt-0.5" />
                        <span className="text-lg font-extralight text-neutral-200">
                          {typeof step === "string" ? step : step.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resources.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 className="w-4 h-4 text-blue-400" />
                    <span className="text-lg font-extralight text-neutral-400">Полезные ресурсы</span>
                  </div>
                  <div className="space-y-2">
                    {resources.map((link, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-md bg-neutral-800/30">
                        <Link2 className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <span className="text-lg font-extralight text-neutral-200 flex-1">
                          {link.title}
                        </span>
                        {link.url && (
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {materials.published_at && (
                <div className="flex items-center gap-2 pt-2 border-t border-neutral-800/50">
                  <Calendar className="w-4 h-4 text-neutral-600" />
                  <span className="text-lg font-extralight text-neutral-600">
                    Опубликовано {new Date(materials.published_at).toLocaleDateString("ru-RU")}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
